'use client';

import { useState } from 'react';
import Image from 'next/image';
import { faArrowRight, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { CityBranch } from '@/app/(main)/(pages)/busca/[slug]/_services/get-city-branches.service';
import { Button } from '@/components/ui/button/button.component';
import { Link } from '@/components/ui/link/link.component';
import { Icon } from '@/components/ui/icon.component';
import { EntityAvatar } from '@/components/ui/avatar/entity-avatar';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { formatAddress, formatGoogleMapsAddress, formatSimpleAddress } from '@/utils/formatters/format-address';
import { formatBranchName } from '@/utils/formatters/branch';
import { generateWhatsAppUrl } from '@/utils/formatters/phonenumber';
import { branchDefaultWhatsappMessage } from '@/utils/consts/cta-whatsapp-messages.consts';
import { googleMapsBaseUrl } from '@/utils/consts/google-maps.consts';
import { cn } from '@/utils/cn-tw-merge';

interface CityBranchCardProps {
  branch: CityBranch;
  citySlug: string | null;
  isPreview?: boolean;
}

export function CityBranchCard({ branch, citySlug, isPreview = false }: CityBranchCardProps) {
  const [expanded, setExpanded] = useState(false);

  const branchName = formatBranchName(branch.name);
  const simpleAddress = formatSimpleAddress(branch.address);
  const fullAddress = formatAddress(branch.address);
  const mapUrl = `${googleMapsBaseUrl}Medprev ${formatGoogleMapsAddress(branch.address)}`;
  const branchUrl = citySlug && branch.slug ? `/cidades/${citySlug}/${branch.slug}` : null;
  const whatsappUrl = branch.whatsapp
    ? generateWhatsAppUrl(branch.whatsapp, branchDefaultWhatsappMessage)
    : null;

  const avatarClassName = cn(
    'block size-22 shrink-0 overflow-hidden rounded-lg border border-gray-300 bg-gray-100',
    'lg:row-span-2',
  );
  const nameClassName = 'line-clamp-3 text-lg font-semibold leading-6 text-green-500';

  const branchAvatar = branch.avatar
    ? (
        <Image
          src={branch.avatar}
          width={88}
          height={88}
          alt={`Foto de perfil de ${branchName}`}
          draggable={false}
          unoptimized
          className="size-full object-cover"
        />
      )
    : (
        <EntityAvatar
          width={88}
          height={88}
          alt={`Foto de perfil de ${branchName}`}
          type={EntityTypeEnum.BRANCH}
        />
      );

  return (
    <div
      data-testid={DataTestIdEnum.SEARCH_PAGE_SUGGESTION_CITY_BRANCH_CARD}
      className={cn(
        'flex w-full flex-col gap-4 rounded-2xl border border-gray-300 bg-white p-3 sm:p-6',
        'xl:flex-row xl:items-start xl:gap-6',
      )}
    >
      <div className="grid grid-cols-[auto_1fr] items-start gap-x-4 gap-y-2 xl:flex-1">
        {branchUrl
          ? (
              <>
                <Link
                  prefetch={false}
                  href={branchUrl}
                  data-testid={DataTestIdEnum.SEARCH_PAGE_SUGGESTION_CITY_BRANCH_PROFILE}
                  className={avatarClassName}
                >
                  {branchAvatar}
                </Link>
                <Link
                  prefetch={false}
                  href={branchUrl}
                  data-testid={DataTestIdEnum.SEARCH_PAGE_SUGGESTION_CITY_BRANCH_PROFILE}
                  className={cn(nameClassName, 'hover:underline')}
                >
                  {branchName}
                </Link>
              </>
            )
          : (
              <>
                <div className={avatarClassName}>{branchAvatar}</div>
                <p className={nameClassName}>{branchName}</p>
              </>
            )}

        <div className="col-span-2 lg:col-span-1 lg:col-start-2">
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            className="flex w-full items-center gap-1 text-left md:hidden"
          >
            <span className="flex-1 truncate text-base leading-6 text-gray-700">
              {simpleAddress}
            </span>
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-200">
              <Icon
                icon={faChevronDown}
                className={cn('text-gray-700 transition-transform duration-300', expanded && 'rotate-180')}
              />
            </span>
          </button>

          <div
            className={cn(
              'overflow-hidden transition-all duration-500 ease-in-out',
              'md:visible md:max-h-none md:opacity-100',
              expanded ? 'visible max-h-screen opacity-100' : 'invisible max-h-0 opacity-0',
            )}
          >
            <p className="mb-1 mt-2 break-words text-base leading-6 text-gray-700 md:mt-0">{fullAddress}</p>
            <Link
              prefetch={false}
              data-testid={DataTestIdEnum.SEARCH_PAGE_SUGGESTION_CITY_BRANCH_MAP}
              target="_blank"
              rel="noopener"
              href={mapUrl}
              className="font-semibold leading-6 text-green-500 hover:underline"
            >
              Abrir no mapa
              {' '}
              <Icon icon={faArrowRight} className="ml-1 text-green-500" />
            </Link>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:gap-4 xl:flex-1 xl:flex-col xl:gap-2">
        {whatsappUrl && (
          <Link
            prefetch={false}
            target="_blank"
            rel="noopener"
            href={whatsappUrl}
            className={cn('w-full md:flex-1', isPreview && 'hidden xl:block')}
          >
            <Button
              data-testid={DataTestIdEnum.SEARCH_PAGE_SUGGESTION_CITY_BRANCH_WHATSAPP}
              className="h-12 w-full"
              startIcon={faWhatsapp}
              startIconClassName="max-w-5 max-h-5"
            >
              Quero agendar
            </Button>
          </Link>
        )}

        {branchUrl && (
          <Link prefetch={false} href={branchUrl} className="w-full md:flex-1">
            <Button
              variant="text"
              data-testid={DataTestIdEnum.SEARCH_PAGE_SUGGESTION_CITY_BRANCH_VIEW}
              className="h-12 w-full"
              startIcon={faArrowRight}
              startIconClassName="max-w-5 max-h-5"
            >
              Ver unidade
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}
